import {calculateShootingDamage} from "../../gameState/shooting/calculateShootingDamage";
import {getUnitById} from "../../unit/getUnitById";
import {isUnitFaction} from "../../factions/isUnitFaction";
import {calculateOrcLoss} from "../../factions/orcs/calculateOrcLoss";
import {FACTIONS} from "../../../constants/factions";
import {SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {FORTRESS_MAX_DAMAGE} from "../../../configs/specialBuildings";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../../../configs/war";

/**
 * Польза уже сделанного выстрела бота в зависимости от граней костей — для
 * решения о перебросе (см. selectBotRerollDice). Урон считается
 * настоящим calculateShootingDamage (худшая/лучшая кость, эльфы, демоны), а
 * пользой считается только то, что выстрел реально сделает при применении
 * (APPLY_SHOOTING_ACTIONS):
 *  - по отряду — потеря численности цели сверх урона остальных выстрелов по
 *    ней, не больше оставшейся численности (урон сверх уничтожения пропадает;
 *    у орков потеря меньше урона, см. calculateOrcLoss);
 *  - по крепости — урон, но не больше ее оставшейся прочности;
 *  - по остальным строениям — пробит ли порог урона строению (1 или 0).
 * Урон строению на ячейке отряда-цели не учитывается: решает урон по отряду.
 *
 * @returns {function(number, number): number}
 */
export const createBotShotValueGetter = ({
  shooter, action, units, players, pendingMovementActions, pendingShootingActions,
  specialBuildings, buildingsDamage, targetUnitOwnerId, targetHexOwnerId,
}) => {
  const calcDamage = (diceRoll1, diceRoll2) => calculateShootingDamage(
    shooter, pendingMovementActions, diceRoll1, diceRoll2, units, players, targetUnitOwnerId, targetHexOwnerId
  ).damage;
  const otherShots = (pendingShootingActions || []).filter((otherShot) => otherShot.id !== action.id);

  const targetUnit = action.targetUnitId ? getUnitById(units, action.targetUnitId) : null;
  if (targetUnit) {
    const calcSizeLoss = (damage) => (
      isUnitFaction(targetUnit, FACTIONS.ORCS) ? calculateOrcLoss(damage) : damage
    );
    const otherDamage = otherShots
      .filter((otherShot) => otherShot.targetUnitId === targetUnit.id)
      .reduce((sum, otherShot) => sum + otherShot.damage, 0);
    const otherSizeLoss = Math.min(targetUnit.size, calcSizeLoss(otherDamage));

    return (diceRoll1, diceRoll2) => (
      Math.min(targetUnit.size, calcSizeLoss(otherDamage + calcDamage(diceRoll1, diceRoll2))) - otherSizeLoss
    );
  }

  if (specialBuildings?.[action.targetHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS) {
    const otherFortressDamage = otherShots
      .filter((otherShot) => otherShot.targetHexKey === action.targetHexKey)
      .reduce((sum, otherShot) => sum + otherShot.damage, 0);
    const remainingDurability = Math.max(
      0, FORTRESS_MAX_DAMAGE - (buildingsDamage?.[action.targetHexKey] || 0) - otherFortressDamage
    );

    return (diceRoll1, diceRoll2) => Math.min(remainingDurability, calcDamage(diceRoll1, diceRoll2));
  }

  return (diceRoll1, diceRoll2) => (
    calcDamage(diceRoll1, diceRoll2) >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE ? 1 : 0
  );
};
